import api from "./api";

export interface ComplianceWorkflow {
	id: string;
	name: string;
	status: "draft" | "active" | "paused" | "completed" | "cancelled" | "deleted";
	jurisdiction?: string;
	device_class?: string;
	device_type?: string;
	device_category?: string;
	completion_percentage: number;
	estimated_duration_days: number;
	target_completion_date?: string | null;
	current_step_id?: string | null;
}

export interface ComplianceStep {
	id: string;
	step_number: number;
	title: string;
	status: "pending" | "in_progress" | "completed" | "blocked" | "skipped";
	estimated_days: number;
	actual_days?: number | null;
	due_date?: string | null;
	required_forms: string[];
	required_documents: string[];
	comments?: string | null;
	attachments?: string[];
}

export interface ComplianceForm {
	id: string;
	form_code: string;
	form_name: string;
	form_description: string;
	jurisdiction: string;
	form_type: string;
	form_category: string;
	required_fields: any[];
	optional_fields: any[];
	supporting_documents: string[];
	file_url?: string;
}

export interface ServiceProvider {
	id: string;
	name: string;
	provider_type: string;
	jurisdictions: string[];
	specializations: string[];
	certifications: string[];
	contact_info: {
		email?: string;
		phone?: string;
		address?: string;
	};
	rating: number;
	availability: string;
	description: string;
	website: string;
	location: {
		country?: string;
		city?: string;
		region?: string;
	};
}

export interface StandardsCatalogEntry {
	code: string;
	title?: string | null;
	organization?: string | null;
	edition_year?: number | null;
	jurisdiction?: string | null;
	source?: string | null;
	source_url?: string | null;
	last_fetched_at?: string | null;
	clause_count: number;
}

interface WorkflowPagination {
	page: number;
	limit: number;
	total: number;
	pages: number;
	has_next: boolean;
	has_prev: boolean;
}

interface WorkflowListResponse {
	workflows: ComplianceWorkflow[];
	pagination: WorkflowPagination;
}

interface WorkflowStatusResponse {
	workflow: ComplianceWorkflow;
	steps: ComplianceStep[];
}

interface FormTemplatesResponse {
	forms: ComplianceForm[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		pages: number;
		has_next: boolean;
		has_prev: boolean;
	};
}

export const complianceApi = {
	getWorkflows: async (
		page = 1,
		limit = 10
	): Promise<WorkflowListResponse> => {
		const response = await api.get<WorkflowListResponse>(
			"/api/v1/compliance/workflows",
			{
				params: { page, limit },
			}
		);
		return response.data;
	},

	getWorkflowStatus: async (
		workflowId: string
	): Promise<WorkflowStatusResponse> => {
		const response = await api.get<WorkflowStatusResponse>(
			`/api/v1/compliance/workflows/${workflowId}/status`
		);
		return response.data;
	},

	startWorkflow: async (workflowId: string): Promise<ComplianceWorkflow> => {
		const response = await api.post<ComplianceWorkflow>(
			`/api/v1/compliance/workflows/${workflowId}/start`
		);
		return response.data;
	},

	pauseWorkflow: async (workflowId: string): Promise<ComplianceWorkflow> => {
		const response = await api.post<ComplianceWorkflow>(
			`/api/v1/compliance/workflows/${workflowId}/pause`
		);
		return response.data;
	},

	createWorkflow: async (data: {
		name: string;
		jurisdiction: string;
		device_class: string;
		device_type?: string;
		device_category?: string;
	}): Promise<ComplianceWorkflow> => {
		const response = await api.post<ComplianceWorkflow>(
			"/api/v1/compliance/workflows",
			data
		);
		return response.data;
	},

	getFormTemplates: async (
		page = 1,
		limit = 20
	): Promise<FormTemplatesResponse> => {
		const response = await api.get<FormTemplatesResponse>(
			"/api/v1/compliance/forms/templates",
			{
				params: { page, limit },
			}
		);
		return response.data;
	},

	downloadForm: async (formId: string): Promise<{
		content: string;
		filename: string;
		content_type: string;
	}> => {
		const response = await api.get<{
			content: string;
			filename: string;
			content_type: string;
		}>(`/api/v1/compliance/forms/${formId}/download`);
		return response.data;
	},

	getServiceProviders: async (): Promise<ServiceProvider[]> => {
		const response = await api.get<ServiceProvider[]>(
			"/api/v1/compliance/providers"
		);
		return response.data;
	},

	listStandardsCatalog: async (): Promise<{ standards: StandardsCatalogEntry[] }> => {
		const response = await api.get<{ standards: StandardsCatalogEntry[] }>(
			"/api/v1/compliance/standards"
		);
		return response.data;
	},

	fetchStandardsCatalog: async (): Promise<{
		fetched_at: string;
		standards_found: number;
		created: number;
		updated: number;
		source_urls_used: string[];
	}> => {
		const response = await api.post<{
			fetched_at: string;
			standards_found: number;
			created: number;
			updated: number;
			source_urls_used: string[];
		}>("/api/v1/compliance/standards/fetch");
		return response.data;
	},

	extractStandardClausesFromPdf: async (
		standardCode: string,
		params?: { replace?: boolean; max_pages?: number }
	): Promise<{
		standard: string;
		inserted: number;
		skipped: number;
		last_fetched_at: string;
	}> => {
		const response = await api.post<{
			standard: string;
			inserted: number;
			skipped: number;
			last_fetched_at: string;
		}>(`/api/v1/compliance/standards/${encodeURIComponent(standardCode)}/clauses/extract-from-pdf`, null, {
			params: {
				replace: params?.replace ?? true,
				max_pages: params?.max_pages ?? 35,
			},
		});
		return response.data;
	},
};

export type {
	WorkflowPagination,
	WorkflowListResponse,
	WorkflowStatusResponse,
	FormTemplatesResponse,
};

